import { supabase } from '../lib/supabase'
import {
  buildGerenciarPortalBody,
  extractContactIdFromPayload,
  gerenciarPortalContato,
  isContactChangeRequestType,
  type BrokerContactForScgp,
  type GerenciarPortalResult,
} from './scgpCorretorService'

export type BrokerChangeRequestType =
  | 'broker_profile'
  | 'contact_create'
  | 'contact_update'
  | 'contact_set_active'
  | 'contact_delete'

export type BrokerChangeRequestStatus = 'pending' | 'applied' | 'rejected' | 'cancelled'

export type BrokerProfilePayload = {
  holder_name?: string
  phone?: string
  email?: string
  notes?: string | null
}

export type ContactCreatePayload = {
  broker_id?: string
  name: string
  phone: string
  whatsapp?: string | null
  email?: string | null
  job_title?: string | null
  department?: string | null
  notes?: string | null
}

export type ContactUpdatePayload = {
  contact_id: string
  name?: string
  phone?: string
  whatsapp?: string | null
  email?: string | null
  job_title?: string | null
  department?: string | null
  notes?: string | null
}

export type ContactSetActivePayload = {
  contact_id: string
  is_active: boolean
}

export type ContactDeletePayload = {
  contact_id: string
}

export type BrokerChangeRequestPayload =
  | BrokerProfilePayload
  | ContactCreatePayload
  | ContactUpdatePayload
  | ContactSetActivePayload
  | ContactDeletePayload
  | Record<string, unknown>

export type BrokerChangeRequestPerson = {
  id: string
  name: string | null
}

export type BrokerChangeRequestBroker = {
  id: string
  name: string | null
  id_scgp: number | null
}

export type HelpdeskBrokerChangeRequest = {
  id: string
  broker_id: string
  request_type: BrokerChangeRequestType
  payload: BrokerChangeRequestPayload
  status: BrokerChangeRequestStatus
  requested_by: string
  requested_at: string
  processed_at: string | null
  processed_by: string | null
  rejection_reason: string | null
  external_ref: string | null
  broker?: BrokerChangeRequestBroker | null
  requester?: BrokerChangeRequestPerson | null
  processor?: BrokerChangeRequestPerson | null
}

export type ListChangeRequestsFilters = {
  status?: BrokerChangeRequestStatus | 'all'
  requestType?: BrokerChangeRequestType | 'all'
  query?: string
}

export type ApplyChangeRequestResult = HelpdeskBrokerChangeRequest & {
  scgpGerenciarPortal?: GerenciarPortalResult | null
}

/** Valores atuais no cadastro antes de aplicar a solicitação (para diff no modal). */
export type ChangeRequestBaseline = Record<string, unknown>

const CHANGE_REQUEST_SELECT = `
  id,
  broker_id,
  request_type,
  payload,
  status,
  requested_by,
  requested_at,
  processed_at,
  processed_by,
  rejection_reason,
  external_ref,
  broker:brokers(id, name, id_scgp),
  requester:profiles!helpdesk_broker_change_requests_requested_by_fkey(id, name),
  processor:profiles!helpdesk_broker_change_requests_processed_by_fkey(id, name)
`

const APPLY_REQUEST_SELECT = `
  id,
  broker_id,
  request_type,
  payload,
  status,
  broker:brokers(id_scgp)
`

const BROKER_CONTACT_DB_SELECT =
  'name, phone, email, department, role, is_partner, contact_code'

const BROKER_CONTACT_SCGP_SELECT = BROKER_CONTACT_DB_SELECT

async function getCurrentUserId(): Promise<string> {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) throw error
  if (!user?.id) throw new Error('Usuário autenticado não encontrado.')
  return user.id
}

function matchesQuery(row: HelpdeskBrokerChangeRequest, query?: string): boolean {
  const q = query?.trim().toLowerCase()
  if (!q) return true

  const brokerName = row.broker?.name?.toLowerCase() ?? ''
  const brokerScgp = row.broker?.id_scgp != null ? String(row.broker.id_scgp) : ''
  const requesterName = row.requester?.name?.toLowerCase() ?? ''

  return brokerName.includes(q) || brokerScgp.includes(q) || requesterName.includes(q)
}

async function insertChangeRequest<TPayload extends BrokerChangeRequestPayload>(
  brokerId: string,
  requestType: BrokerChangeRequestType,
  payload: TPayload
): Promise<HelpdeskBrokerChangeRequest> {
  const requestedBy = await getCurrentUserId()
  const { data, error } = await supabase
    .from('helpdesk_broker_change_requests')
    .insert({
      broker_id: brokerId,
      request_type: requestType,
      payload,
      requested_by: requestedBy,
    })
    .select(CHANGE_REQUEST_SELECT)
    .single()

  if (error) throw error
  return data as unknown as HelpdeskBrokerChangeRequest
}

type ChangeRequestRowForApply = {
  id: string
  broker_id: string
  request_type: BrokerChangeRequestType
  payload: BrokerChangeRequestPayload
  status: BrokerChangeRequestStatus
  broker?: { id_scgp: number | null } | null
}

async function fetchBrokerContactForApply(
  brokerId: string,
  contactId: string
): Promise<BrokerContactForScgp> {
  const { data, error } = await supabase
    .from('broker_contacts')
    .select(BROKER_CONTACT_SCGP_SELECT)
    .eq('id', contactId)
    .eq('broker_id', brokerId)
    .maybeSingle()

  if (error) throw error
  if (!data) {
    throw new Error('Contato não encontrado para aplicar a solicitação.')
  }
  return data as BrokerContactForScgp
}

function resolveCodCorretor(broker: ChangeRequestRowForApply['broker']): number {
  const idScgp = broker?.id_scgp
  if (idScgp == null || !Number.isFinite(Number(idScgp))) {
    throw new Error('Corretor sem código SCGP (id_scgp). Não é possível aplicar no portal.')
  }
  return Number(idScgp)
}

function buildExternalRef(scgpResult: GerenciarPortalResult): string {
  try {
    return JSON.stringify({
      tipoOperacao: scgpResult.tipoOperacao,
      codContato: scgpResult.codContato ?? null,
    })
  } catch {
    return scgpResult.tipoOperacao
  }
}

export const brokerCadastroChangeRequestsService = {
  async submitBrokerProfileChange(
    brokerId: string,
    payload: BrokerProfilePayload
  ): Promise<HelpdeskBrokerChangeRequest> {
    return insertChangeRequest(brokerId, 'broker_profile', payload)
  },

  async submitContactCreate(
    brokerId: string,
    payload: ContactCreatePayload
  ): Promise<HelpdeskBrokerChangeRequest> {
    return insertChangeRequest(brokerId, 'contact_create', {
      ...payload,
      broker_id: brokerId,
    })
  },

  async submitContactUpdate(
    brokerId: string,
    payload: ContactUpdatePayload
  ): Promise<HelpdeskBrokerChangeRequest> {
    return insertChangeRequest(brokerId, 'contact_update', payload)
  },

  async submitContactSetActive(
    brokerId: string,
    contactId: string,
    isActive: boolean
  ): Promise<HelpdeskBrokerChangeRequest> {
    return insertChangeRequest(brokerId, 'contact_set_active', {
      contact_id: contactId,
      is_active: isActive,
    })
  },

  async submitContactDelete(
    brokerId: string,
    contactId: string
  ): Promise<HelpdeskBrokerChangeRequest> {
    return insertChangeRequest(brokerId, 'contact_delete', {
      contact_id: contactId,
    })
  },

  async listChangeRequests(filters: ListChangeRequestsFilters = {}): Promise<HelpdeskBrokerChangeRequest[]> {
    const pageSize = 1000
    let page = 0
    let hasMore = true
    const allRows: HelpdeskBrokerChangeRequest[] = []

    while (hasMore) {
      const from = page * pageSize
      const to = from + pageSize - 1
      let query = supabase
        .from('helpdesk_broker_change_requests')
        .select(CHANGE_REQUEST_SELECT)
        .order('requested_at', { ascending: false })
        .range(from, to)

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status)
      }

      if (filters.requestType && filters.requestType !== 'all') {
        query = query.eq('request_type', filters.requestType)
      }

      const { data, error } = await query
      if (error) throw error

      const batch = (data || []) as unknown as HelpdeskBrokerChangeRequest[]
      allRows.push(...batch)

      if (batch.length < pageSize) {
        hasMore = false
      } else {
        page += 1
      }
    }

    return allRows.filter((row) => matchesQuery(row, filters.query))
  },

  async getBrokerChangeHistory(brokerId: string): Promise<HelpdeskBrokerChangeRequest[]> {
    const { data, error } = await supabase
      .from('helpdesk_broker_change_requests')
      .select(CHANGE_REQUEST_SELECT)
      .eq('broker_id', brokerId)
      .order('requested_at', { ascending: false })

    if (error) throw error
    return (data || []) as unknown as HelpdeskBrokerChangeRequest[]
  },

  async getChangeRequestBaseline(
    request: Pick<HelpdeskBrokerChangeRequest, 'broker_id' | 'request_type' | 'payload'>
  ): Promise<ChangeRequestBaseline | null> {
    const { broker_id: brokerId, request_type: requestType, payload } = request

    if (requestType === 'broker_profile') {
      const { data, error } = await supabase
        .from('brokers')
        .select('holder_name')
        .eq('id', brokerId)
        .maybeSingle()
      if (error) throw error
      if (!data) return null
      return { holder_name: data.holder_name }
    }

    if (requestType === 'contact_create') {
      return null
    }

    const contactId = extractContactIdFromPayload(payload as Record<string, unknown>)
    if (!contactId) return null

    const { data, error } = await supabase
      .from('broker_contacts')
      .select(BROKER_CONTACT_DB_SELECT)
      .eq('id', contactId)
      .eq('broker_id', brokerId)
      .maybeSingle()

    if (error) throw error
    if (!data) return null

    return {
      name: data.name,
      phone: data.phone,
      email: data.email,
      department: data.department,
      job_title: data.role,
      whatsapp: null,
      notes: null,
      is_partner: data.is_partner,
      contact_code: data.contact_code,
      is_active: null,
    }
  },

  async applyChangeRequest(requestId: string): Promise<ApplyChangeRequestResult> {
    const { data: row, error: fetchErr } = await supabase
      .from('helpdesk_broker_change_requests')
      .select(APPLY_REQUEST_SELECT)
      .eq('id', requestId)
      .single()

    if (fetchErr) throw fetchErr
    const typed = row as unknown as ChangeRequestRowForApply
    if (typed.status !== 'pending') {
      throw new Error('Esta solicitação não está mais pendente ou não foi encontrada.')
    }

    const requiresScgp = isContactChangeRequestType(typed.request_type)
    let scgpResult: GerenciarPortalResult | null = null
    let externalRef: string | null = null

    if (isContactChangeRequestType(typed.request_type)) {
      const codCorretor = resolveCodCorretor(typed.broker)
      const contactId = extractContactIdFromPayload(typed.payload as Record<string, unknown>)
      const contact =
        contactId != null ? await fetchBrokerContactForApply(typed.broker_id, contactId) : null

      const body = buildGerenciarPortalBody(
        typed.request_type,
        typed.payload as Record<string, unknown>,
        codCorretor,
        contact
      )
      scgpResult = await gerenciarPortalContato(body)
      externalRef = buildExternalRef(scgpResult)
    }

    if (requiresScgp && !scgpResult) {
      throw new Error(
        'A solicitação só pode ser marcada como aplicada após sucesso no SCGP (GerenciarPortal).'
      )
    }

    const processedBy = await getCurrentUserId()
    const { data, error } = await supabase
      .from('helpdesk_broker_change_requests')
      .update({
        status: 'applied',
        processed_at: new Date().toISOString(),
        processed_by: processedBy,
        rejection_reason: null,
        external_ref: externalRef,
      })
      .eq('id', requestId)
      .eq('status', 'pending')
      .select(CHANGE_REQUEST_SELECT)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Esta solicitação não está mais pendente ou não foi encontrada.')
      }
      throw error
    }

    return {
      ...(data as unknown as HelpdeskBrokerChangeRequest),
      scgpGerenciarPortal: scgpResult,
    }
  },

  async cancelChangeRequest(requestId: string, reason: string): Promise<HelpdeskBrokerChangeRequest> {
    const trimmed = reason.trim()
    if (!trimmed) {
      throw new Error('Informe o motivo do cancelamento.')
    }
    if (trimmed.length < 3) {
      throw new Error('O motivo do cancelamento deve ter pelo menos 3 caracteres.')
    }

    const processedBy = await getCurrentUserId()
    const { data, error } = await supabase
      .from('helpdesk_broker_change_requests')
      .update({
        status: 'cancelled',
        processed_at: new Date().toISOString(),
        processed_by: processedBy,
        rejection_reason: trimmed,
      })
      .eq('id', requestId)
      .eq('status', 'pending')
      .select(CHANGE_REQUEST_SELECT)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Esta solicitação não está mais pendente ou não foi encontrada.')
      }
      throw error
    }
    return data as unknown as HelpdeskBrokerChangeRequest
  },

  async rejectChangeRequest(requestId: string, reason: string): Promise<HelpdeskBrokerChangeRequest> {
    const trimmed = reason.trim()
    if (!trimmed) {
      throw new Error('Informe o motivo da rejeição.')
    }
    if (trimmed.length < 3) {
      throw new Error('O motivo da rejeição deve ter pelo menos 3 caracteres.')
    }

    const processedBy = await getCurrentUserId()
    const { data, error } = await supabase
      .from('helpdesk_broker_change_requests')
      .update({
        status: 'rejected',
        processed_at: new Date().toISOString(),
        processed_by: processedBy,
        rejection_reason: trimmed,
      })
      .eq('id', requestId)
      .eq('status', 'pending')
      .select(CHANGE_REQUEST_SELECT)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Esta solicitação não está mais pendente ou não foi encontrada.')
      }
      throw error
    }
    return data as unknown as HelpdeskBrokerChangeRequest
  },
}

export default brokerCadastroChangeRequestsService
